import { useId, useRef } from "react";

import { cn } from "../../lib/cn.js";

/**
 * shadcn/ui's Tabs look (a muted rail, the active tab raised), without Radix. Controlled: the
 * caller owns which tab is open. Arrow keys move between tabs, as the WAI-ARIA tabs pattern asks.
 */
export interface TabItem<K extends string> {
  readonly value: K;
  readonly label: React.ReactNode;
}

export function Tabs<K extends string>({
  items,
  value,
  onValueChange,
  label,
  className,
  children,
}: {
  readonly items: readonly TabItem<K>[];
  readonly value: K;
  readonly onValueChange: (value: K) => void;
  /** What the tab list is, for screen readers. */
  readonly label: string;
  readonly className?: string;
  /** The open tab's panel. */
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const id = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, step: number): void => {
    const next = (from + step + items.length) % items.length;
    const item = items[next];
    if (item === undefined) return;
    onValueChange(item.value);
    refs.current[next]?.focus();
  };

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div role="tablist" aria-label={label} className="bg-muted text-muted-foreground inline-flex h-9 w-fit items-center rounded-lg p-[3px]">
        {items.map((item, index) => {
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              ref={(element) => {
                refs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={`${id}-tab-${item.value}`}
              aria-selected={selected}
              aria-controls={`${id}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onValueChange(item.value)}
              onKeyDown={(event) => {
                if (event.key === "ArrowRight") move(index, 1);
                if (event.key === "ArrowLeft") move(index, -1);
              }}
              className={cn(
                "inline-flex h-full cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent px-3 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none",
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] [&_svg]:size-4 [&_svg]:shrink-0",
                selected ? "bg-background text-foreground shadow-sm dark:border-input" : "hover:text-foreground",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${value}`}>
        {children}
      </div>
    </div>
  );
}
